import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import Avatar from '../../components/Avatar';
import { 
  Search, 
  User, 
  Phone, 
  Mail, 
  MapPin,
  Eye,
  EyeOff,
  Lock
} from 'lucide-react';

function ContractorCard({ contractor, isAdmin }) {
  const hasContact = contractor.shareContactInfo || isAdmin;
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <Avatar name={contractor.displayName || contractor.name} photo={contractor.profilePhoto} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{contractor.displayName || contractor.name}</h3>
            {contractor.shareContactInfo && (
              <Eye className="w-4 h-4 text-green-500" title="Shares contact info" />
            )}
          </div>
          
          {hasContact ? (
            <div className="mt-2 space-y-1">
              {contractor.phone && (
                <a href={`tel:${contractor.phone}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-hub-600">
                  <Phone className="w-4 h-4" />
                  {contractor.phone}
                </a>
              )}
              {contractor.email && (
                <a href={`mailto:${contractor.email}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-hub-600">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{contractor.email}</span>
                </a>
              )}
              {isAdmin && contractor.address && (
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>{contractor.address}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-400">
              <Lock className="w-4 h-4" />
              <span>Contact info hidden</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ContractorDirectory() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharingEnabled, setSharingEnabled] = useState(false);
  const [updating, setUpdating] = useState(false);

  const isAdmin = ['OWNER', 'ADMIN'].includes(user?.role);

  useEffect(() => {
    // Fetch current sharing status
    api.get('/directory/contractors/privacy').then(data => {
      setSharingEnabled(data.shareContactInfo || false);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadContractors();
  }, [search]);

  const loadContractors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const result = await api.get(`/directory/contractors?${params.toString()}`);
      setContractors(result);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const toggleSharing = async () => {
    setUpdating(true);
    try {
      const result = await api.put('/directory/contractors/privacy', {
        shareContactInfo: !sharingEnabled
      });
      setSharingEnabled(result.shareContactInfo);
      loadContractors(); // Refresh to update visibility
    } catch (err) {
      console.error(err);
    }
    setUpdating(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Contractor Directory</h1>
        
        {/* Privacy Toggle - only for contractors */}
        {!isAdmin && (
          <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              {sharingEnabled ? (
                <Eye className="w-5 h-5 text-green-500" />
              ) : (
                <EyeOff className="w-5 h-5 text-gray-400" />
              )}
              <span className="text-sm text-gray-700">
                {sharingEnabled ? 'Sharing my contact info' : 'Contact info hidden'}
              </span>
            </div>
            <button
              onClick={toggleSharing}
              disabled={updating}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                sharingEnabled
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {updating ? '...' : (sharingEnabled ? 'Turn Off' : 'Turn On')}
            </button>
          </div>
        )}
      </div>

      {/* Info banner for contractors */}
      {!isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p>
            {sharingEnabled 
              ? "Your contact info is visible to other contractors."
              : "Your contact info is hidden. Turn on sharing to let others reach you."}
          </p>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search contractors..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10 w-full"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
        </div>
      ) : contractors.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No contractors found</p>
          {search && <p className="text-sm">Try a different search term</p>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contractors.map(contractor => (
            <ContractorCard
              key={contractor.id}
              contractor={contractor}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
}
